import {
    type TreeNode,
    type TreeNodeType,
    type NodeStyle,
    type TreeFont,
    type TreeType,
    type TreePatch,
    type TreeSnapshot,
    type VersionedValue,
    type DomChangeEvent,
    type DomChangeEventType,
    type PatchAckResult,
    TreeLimits,
} from "./Types.ts";
import { restoreNodeMaps } from "./HtmlParser.ts";

// ─── DomTree ──────────────────────────────────────────────────────────────────
//
// The core VDOM data structure. It is the single source of truth for the page.
//
// Responsibilities:
//   - Stores the node tree (root + flat nodes Map for O(1) lookup)
//   - Applies structural mutations: add, remove, move
//   - Applies LWW property updates: attributes, classes, text, tagName, comment,
//     custom styles, builder styles, fonts
//   - Validates patches against TreeLimits and seq continuity
//   - Emits DomChangeEvent for every accepted mutation
//
// What DomTree does NOT do:
//   - No networking — it receives patches forwarded by the caller
//   - No undo/redo history — that lives in UndoStack
//   - No pending-patch tracking — that lives in PatchQueue

export class DomTree {
    public readonly id: string;
    public readonly pageType: TreeType;

    // Last page seq applied from the server. Used for reconnect catchup.
    public seq: number;

    // Page-level font registry.
    public readonly fonts: Map<string, TreeFont>;

    // Builder-generated styles keyed by nodeId.
    // Each array holds one NodeStyle per (property, pseudo, media, theme) combination.
    public readonly builderStyles: Map<string, NodeStyle[]>;

    // The root document node. Its type is always "document".
    public root: TreeNode;

    // Flat lookup map — O(1) node access by id.
    private readonly nodes: Map<string, TreeNode>;

    // Event listeners keyed by event type name.
    private readonly listeners: Map<string, Set<(event: DomChangeEvent) => void>>;

    constructor(id: string, pageType: TreeType = "document") {
        this.id = id;
        this.pageType = pageType;
        this.seq = 0;
        this.fonts = new Map();
        this.builderStyles = new Map();
        this.listeners = new Map();
        this.nodes = new Map();

        this.root = this.buildDocumentRoot();
        this.nodes.set(this.root.id, this.root);
    }

    // ─── Event system ─────────────────────────────────────────────────────────

    public on(eventType: DomChangeEventType | "change", listener: (event: DomChangeEvent) => void): void {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, new Set());
        }
        this.listeners.get(eventType)!.add(listener);
    }

    public off(eventType: DomChangeEventType | "change", listener: (event: DomChangeEvent) => void): void {
        this.listeners.get(eventType)?.delete(listener);
    }

    private emitValidationFailed(nodeId: string, result: PatchAckResult): void {
        this.emit({ type: "validationFailed", nodeId, validationResult: result });
    }

    private emit(event: DomChangeEvent): void {
        const specificListeners = this.listeners.get(event.type);
        if (specificListeners) {
            for (const listener of specificListeners) {
                listener(event);
            }
        }
        // The "change" channel receives every event regardless of type.
        const changeListeners = this.listeners.get("change");
        if (changeListeners) {
            for (const listener of changeListeners) {
                listener(event);
            }
        }
    }

    // ─── Node factories ───────────────────────────────────────────────────────
    //
    // Each factory generates a UUID v4 id internally. Callers never supply ids.

    public createElementNode(tagName: string, userId: string): TreeNode {
        return this.buildNode("element", userId, tagName);
    }

    public createTextNode(text: string, userId: string): TreeNode {
        const node = this.buildNode("text", userId, "");
        node.text = { version: 0, seq: 0, value: text };
        return node;
    }

    public createCommentNode(comment: string, userId: string): TreeNode {
        const node = this.buildNode("comment", userId, "");
        node.comment = { version: 0, seq: 0, value: comment };
        return node;
    }

    private buildNode(type: TreeNodeType, userId: string, tagName: string): TreeNode {
        const now = Date.now();
        return {
            id: crypto.randomUUID(),
            type,
            parentId: "",
            position: "",
            tagName: { version: 0, seq: 0, value: tagName },
            text: { version: 0, seq: 0, value: null },
            comment: { version: 0, seq: 0, value: null },
            attributes: new Map(),
            classes: new Map(),
            customStyles: new Map(),
            children: [],
            createdUserId: userId,
            createdAt: now,
            updatedUserId: userId,
            updatedAt: now,
        };
    }

    private buildDocumentRoot(): TreeNode {
        const node = this.buildNode("document", "", "");
        node.id = "root";
        return node;
    }

    // ─── Structural mutations ─────────────────────────────────────────────────

    /**
     * Inserts a node into the tree under the given parent.
     *
     * Insertion order is determined by siblingBeforeId and siblingAfterId:
     *   - If siblingBeforeId is found, insert immediately after it.
     *   - Else if siblingAfterId is found, insert immediately before it.
     *   - Otherwise append at the end.
     *
     * node.position is left empty until the server confirms a real LexoRank value.
     */
    public addNode(
        parentId: string,
        node: TreeNode,
        siblingBeforeId?: string,
        siblingAfterId?: string,
    ): DomChangeEvent | null {
        if(!node.id?.trim()){
            this.emitValidationFailed(node.id, "InvalidPatch");
            return null;
        }
        if (this.nodes.size >= TreeLimits.MaxPageNodes) {
            this.emitValidationFailed(node.id, "MaximumReached");
            return null;
        }

        if (this.nodes.has(node.id)) {
            this.emitValidationFailed(node.id, "IdConflict");
            return null;
        }

        const parent = this.nodes.get(parentId);
        if (!parent) {
            this.emitValidationFailed(node.id, "NotFound");
            return null;
        }

        // Reject if any node in the subtree would collide with an existing id.
        const collisionId = this.findSubtreeCollision(node);
        if (collisionId !== null) {
            this.emitValidationFailed(collisionId, "IdConflict");
            return null;
        }

        const insertIndex = this.resolveInsertIndex(parent, siblingBeforeId, siblingAfterId);

        node.parentId = parentId;
        parent.children.splice(insertIndex, 0, node);
        this.registerSubtree(node);

        const event: DomChangeEvent = {
            type: "nodeAdded",
            nodeId: node.id,
            addedNode: node,
            addedToParentId: parentId,
            siblingBeforeId: siblingBeforeId,
            siblingAfterId: siblingAfterId,
        };
        this.emit(event);
        return event;
    }

    /**
     * Removes a node and its entire subtree.
     * The root document node cannot be removed.
     */
    public removeNode(nodeId: string): DomChangeEvent | null {
        if(!nodeId?.trim()){
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }
        // Reject if the node is the root document node. The root cannot be removed.
        if (nodeId === this.root.id) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }
        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        //no parentId means is root node, which cannot be removed
        if (!node.parentId?.trim()) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }

        const savedParentId = node.parentId;

        const parent = this.nodes.get(node.parentId);
        if (parent) {
            const index = parent.children.findIndex(child => child.id === nodeId);
            if (index !== -1) {
                parent.children.splice(index, 1);
            }
        }

        this.unregisterSubtree(node);

        const event: DomChangeEvent = {
            type: "nodeRemoved",
            nodeId,
            oldParentId: savedParentId,
        };
        this.emit(event);
        return event;
    }

    /**
     * Moves an existing node to a new parent or a new position within the same parent.
     * Sibling hints drive insertion using the same logic as addNode.
     * Rejects if the target would create a cycle (moving a node into its own descendant).
     */
    public moveNode(
        nodeId: string,
        newParentId: string,
        siblingBeforeId?: string,
        siblingAfterId?: string,
    ): DomChangeEvent | null {
        if(!nodeId?.trim() || !newParentId?.trim()){
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }
        if (nodeId?.trim() === newParentId?.trim()) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }
        if(siblingBeforeId?.trim() === siblingAfterId?.trim()){
            siblingBeforeId = undefined;
        }
        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        const newParent = this.nodes.get(newParentId);
        if (!newParent) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        // Walk up from newParent — if we reach nodeId, the move would create a cycle.
        const visited = new Set<string>();
        let cursor: TreeNode | undefined = newParent;
        while (cursor) {
            if (visited.has(cursor.id)) {
                // Corrupted parentId chain — stop the walk.
                break;
            }
            visited.add(cursor.id);
            if (cursor.id === nodeId) {
                this.emitValidationFailed(nodeId, "CannotMoveIntoDescendant");
                return null;
            }
            cursor = this.nodes.get(cursor.parentId);
        }

        const oldParentId = node.parentId;

        const oldParent = this.nodes.get(node.parentId);
        if (oldParent) {
            const oldIndex = oldParent.children.findIndex(child => child.id === nodeId);
            if (oldIndex !== -1) {
                oldParent.children.splice(oldIndex, 1);
            }
        }

        const insertIndex = this.resolveInsertIndex(newParent, siblingBeforeId, siblingAfterId);

        node.parentId = newParentId;
        node.position = "";// Clear position until the server confirms a real LexoRank.
        newParent.children.splice(insertIndex, 0, node);

        const event: DomChangeEvent = {
            type: "nodeMoved",
            nodeId,
            oldParentId,
            newParentId,
        };
        this.emit(event);
        return event;
    }

    /**
     * Confirms a server-assigned LexoRank position for one node, or batches
     * positions for an entire added subtree.
     *
     * Single-node form  (move confirmation):
     *   confirmNodePosition(nodeId, serverPosition)
     *   — sets position, sorts the parent once, emits one event.
     *
     * Batch form  (add confirmation):
     *   confirmNodePosition(affectedNodes)  where affectedNodes is the
     *   server-returned list of every node in the added subtree.
     *   — sets all positions first, then sorts each unique parent exactly
     *     once and emits one event per parent, avoiding the O(N²) DOM
     *     re-sort that firing per-node would cause.
     */
    public confirmNodePosition(nodeId: string, serverPosition: string): DomChangeEvent | null;
    public confirmNodePosition(nodes: TreeNode[]): void;
    public confirmNodePosition(
        nodeIdOrNodes: string | TreeNode[],
        serverPosition?: string,
    ): DomChangeEvent | null | void {
        if (Array.isArray(nodeIdOrNodes)) {
            // ── Batch path ────────────────────────────────────────────────────
            const affectedParentIds = new Set<string>();

            for (const affectedNode of nodeIdOrNodes) {
                if (!affectedNode.id || !affectedNode.position) continue;
                const node = this.nodes.get(affectedNode.id);
                if (!node) continue;
                node.position = affectedNode.position;
                if (node.parentId) affectedParentIds.add(node.parentId);
            }

            for (const parentId of affectedParentIds) {
                const parent = this.nodes.get(parentId);
                if (!parent || parent.children.length <= 1) continue;
                parent.children.sort((a, b) =>
                    a.position < b.position ? -1 : a.position > b.position ? 1 : 0,
                );
                this.emit({
                    type: "nodePositionConfirmed",
                    nodeId: parent.children[0].id,
                    serverPosition: parent.children[0].position,
                });
            }
        } else {
            // ── Single-node path ──────────────────────────────────────────────
            const nodeId = nodeIdOrNodes;
            const node = this.nodes.get(nodeId);
            if (!node) return null;

            node.position = serverPosition!;

            const parent = this.nodes.get(node.parentId);
            if (parent && parent.children.length > 1) {
                parent.children.sort((a, b) =>
                    a.position < b.position ? -1 : a.position > b.position ? 1 : 0,
                );
            }

            const event: DomChangeEvent = {
                type: "nodePositionConfirmed",
                nodeId,
                serverPosition: serverPosition!,
            };
            this.emit(event);
            return event;
        }
    }

    // ─── LWW property updates ─────────────────────────────────────────────────
    //
    // Returns null if:
    //   - The target node does not exist.
    //   - incoming.version <= current.version  (LWW rejection — stale packet).
    //   - A TreeLimits constraint is violated.
    // Returns the emitted DomChangeEvent on success.

    public updateAttribute(
        nodeId: string,
        attributeName: string,
        incomingValue: VersionedValue<string | null>,
    ): DomChangeEvent | null {
        const normalizedName = attributeName.toLowerCase();

        if (normalizedName.length > TreeLimits.MaxAttributeKeyLength) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }
        if (incomingValue.value !== null && incomingValue.value.length > TreeLimits.MaxAttributeValueLength) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }

        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        const current = node.attributes.get(normalizedName);

        // LWW rejection — stale or duplicate remote packet. Silent.
        // version === 0 means client-optimistic (server hasn't assigned yet) — always apply.
        if (incomingValue.version > 0 && current !== undefined && incomingValue.version <= current.version) {
            return null;
        }

        if (current === undefined && node.attributes.size >= TreeLimits.MaxAttributeCount) {
            this.emitValidationFailed(nodeId, "MaximumReached");
            return null;
        }

        node.attributes.set(normalizedName, incomingValue);

        const eventType: DomChangeEventType = incomingValue.value === null ? "attributeRemoved" : "attributeSet";
        const event: DomChangeEvent = {
            type: eventType,
            nodeId,
            attributeName: normalizedName,
            attributeValue: incomingValue.value,
        };
        this.emit(event);
        return event;
    }

    public updateClass(
        nodeId: string,
        className: string,
        incomingValue: VersionedValue<boolean>,
    ): DomChangeEvent | null {
        if (className.length > TreeLimits.MaxClassNameLength) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }

        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        const current = node.classes.get(className);

        // LWW rejection — stale or duplicate remote packet. Silent.
        // version === 0 means client-optimistic — always apply.
        if (incomingValue.version > 0 && current !== undefined && incomingValue.version <= current.version) {
            return null;
        }

        if (current === undefined && node.classes.size >= TreeLimits.MaxClassCount) {
            this.emitValidationFailed(nodeId, "MaximumReached");
            return null;
        }

        node.classes.set(className, incomingValue);

        const eventType: DomChangeEventType = incomingValue.value ? "classAdded" : "classRemoved";
        const event: DomChangeEvent = {
            type: eventType,
            nodeId,
            className,
            classActive: incomingValue.value,
        };
        this.emit(event);
        return event;
    }

    public updateText(nodeId: string, incomingValue: VersionedValue<string | null>): DomChangeEvent | null {
        if (incomingValue.value !== null && incomingValue.value.length > TreeLimits.MaxTextLength) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }

        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        // LWW rejection — silent. version === 0 means client-optimistic — always apply.
        if (incomingValue.version > 0 && incomingValue.version <= node.text.version) {
            return null;
        }

        node.text = incomingValue;

        const event: DomChangeEvent = {
            type: "textChanged",
            nodeId,
            textValue: incomingValue.value,
        };
        this.emit(event);
        return event;
    }

    public updateTagName(nodeId: string, incomingValue: VersionedValue<string>): DomChangeEvent | null {
        if (incomingValue.value.length > TreeLimits.MaxTagNameLength) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }

        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        // LWW rejection — silent. version === 0 means client-optimistic — always apply.
        if (incomingValue.version > 0 && incomingValue.version <= node.tagName.version) {
            return null;
        }

        node.tagName = incomingValue;

        const event: DomChangeEvent = {
            type: "tagNameChanged",
            nodeId,
            tagName: incomingValue.value,
        };
        this.emit(event);
        return event;
    }

    public updateComment(nodeId: string, incomingValue: VersionedValue<string | null>): DomChangeEvent | null {
        if (incomingValue.value !== null && incomingValue.value.length > TreeLimits.MaxCommentLength) {
            this.emitValidationFailed(nodeId, "InvalidPatch");
            return null;
        }

        const node = this.nodes.get(nodeId);
        if (!node) {
            this.emitValidationFailed(nodeId, "NotFound");
            return null;
        }

        // LWW rejection — silent. version === 0 means client-optimistic — always apply.
        if (incomingValue.version > 0 && incomingValue.version <= node.comment.version) {
            return null;
        }

        node.comment = incomingValue;

        const event: DomChangeEvent = {
            type: "commentChanged",
            nodeId,
            commentValue: incomingValue.value,
        };
        this.emit(event);
        return event;
    }

    public updateCustomStyle(
        nodeId: string,
        key: string,
        incomingValue: VersionedValue<string>,
    ): DomChangeEvent | null {
        const node = this.nodes.get(nodeId);
        if (!node) {
            return null;
        }

        const current = node.customStyles.get(key);

        if (incomingValue.version > 0 && current !== undefined && incomingValue.version <= current.version) {
            return null;
        }

        node.customStyles.set(key, incomingValue);

        const event: DomChangeEvent = {
            type: "customStyleChanged",
            nodeId,
            customStyleKey: key,
            customStyleValue: incomingValue.value,
        };
        this.emit(event);
        return event;
    }

    // ─── Builder styles ───────────────────────────────────────────────────────
    //
    // Builder styles live at the page level in DomTree.builderStyles, keyed by
    // nodeId. Each entry is an array of NodeStyle objects, one per unique
    // (property, pseudo, media, theme) combination.

    /**
     * Applies a builder style with LWW conflict resolution.
     * value === undefined means the rule has been removed (tombstone).
     */
    public setBuilderStyle(incomingStyle: NodeStyle): DomChangeEvent | null {
        if (incomingStyle.property.length > TreeLimits.MaxStylePropertyLength) {
            this.emitValidationFailed(incomingStyle.nodeId, "MaximumReached");
            return null;
        }
        if (incomingStyle.value !== undefined && incomingStyle.value.length > TreeLimits.MaxStyleValueLength) {
            this.emitValidationFailed(incomingStyle.nodeId, "MaximumReached");
            return null;
        }
        if (incomingStyle.pseudo.length > TreeLimits.MaxStylePseudoLength) {
            this.emitValidationFailed(incomingStyle.nodeId, "MaximumReached");
            return null;
        }
        if (incomingStyle.media.length > TreeLimits.MaxStyleMediaLength) {
            this.emitValidationFailed(incomingStyle.nodeId, "MaximumReached");
            return null;
        }
        if (incomingStyle.theme.length > TreeLimits.MaxStyleThemeLength) {
            this.emitValidationFailed(incomingStyle.nodeId, "MaximumReached");
            return null;
        }

        let styleArray = this.builderStyles.get(incomingStyle.nodeId);
        if (!styleArray) {
            styleArray = [];
            this.builderStyles.set(incomingStyle.nodeId, styleArray);
        }

        const existingIndex = styleArray.findIndex(
            existing =>
                existing.property === incomingStyle.property &&
                existing.pseudo === incomingStyle.pseudo &&
                existing.media === incomingStyle.media &&
                existing.theme === incomingStyle.theme,
        );

        if (existingIndex !== -1) {
            // LWW rejection — silent. version === 0 means client-optimistic — always apply.
            if (incomingStyle.version > 0 && incomingStyle.version <= styleArray[existingIndex].version) {
                return null;
            }
            styleArray[existingIndex] = incomingStyle;
        } else {
            if (styleArray.length >= TreeLimits.MaxStylePropertyCount) {
                this.emitValidationFailed(incomingStyle.nodeId, "MaximumReached");
                return null;
            }
            styleArray.push(incomingStyle);
        }

        const eventType: DomChangeEventType = incomingStyle.value === undefined ? "builderStyleRemoved" : "builderStyleSet";
        const event: DomChangeEvent = {
            type: eventType,
            nodeId: incomingStyle.nodeId,
            nodeStyle: incomingStyle,
        };
        this.emit(event);
        return event;
    }

    public removeBuilderStyle(
        nodeId: string,
        property: string,
        pseudo: string,
        media: string,
        theme: string,
        version: number,
    ): DomChangeEvent | null {
        const styleArray = this.builderStyles.get(nodeId);
        if (!styleArray) {
            return null;
        }

        const existingIndex = styleArray.findIndex(
            existing =>
                existing.property === property &&
                existing.pseudo === pseudo &&
                existing.media === media &&
                existing.theme === theme,
        );

        if (existingIndex === -1) {
            return null;
        }

        // LWW rejection — silent. version === 0 means client-optimistic — always apply.
        if (version > 0 && version <= styleArray[existingIndex].version) {
            return null;
        }

        const removed = styleArray[existingIndex];
        styleArray.splice(existingIndex, 1);

        const event: DomChangeEvent = {
            type: "builderStyleRemoved",
            nodeId,
            nodeStyle: removed,
        };
        this.emit(event);
        return event;
    }

    /**
     * Resolves the effective builder style for a property at a given
     * pseudo/media/theme, cascading through intermediate breakpoints toward
     * the base device when the exact breakpoint has no override of its own.
     *
     * Breakpoints narrower than `baseWidth` cascade DOWN toward the base
     * (checking progressively wider breakpoints); breakpoints wider than
     * `baseWidth` cascade UP toward the base (checking progressively
     * narrower breakpoints) — mirroring the `min-width`/`max-width` CSS
     * IframeBridge compiles for each direction. Pass `baseWidth <= 0` (or
     * omit it) to fall back to a simple "exact breakpoint, then base" lookup.
     *
     * This is the single source of truth for style resolution — callers
     * (e.g. the Builder's StylesheetManager) should use this instead of
     * re-implementing cascade logic of their own.
     *
     * @returns the matching entry (if any) and whether it was found at the
     * exact requested `media` (`ownBreakpoint: true`) or inherited from
     * another breakpoint / the base device (`ownBreakpoint: false`).
     */
    public findBuilderStyle(
        nodeId: string,
        property: string,
        pseudo: string,
        media: string,
        theme: string,
        baseWidth: number = -1,
    ): { entry: NodeStyle | undefined; ownBreakpoint: boolean } {
        const styles = this.builderStyles.get(nodeId) ?? [];
        const availableMedias = new Set(styles.map(s => s.media).filter(m => m !== ""));
        const candidates = cascadeMediaCandidates(media, baseWidth, availableMedias);

        for (let i = 0; i < candidates.length; i++) {
            const found = styles.find(
                s => s.property === property && s.pseudo === pseudo && s.media === candidates[i] && s.theme === theme && s.value != null,
            );
            if (found) {
                return { entry: found, ownBreakpoint: i === 0 };
            }
        }
        return { entry: undefined, ownBreakpoint: true };
    }

    /**
     * Returns all (property, pseudo, theme) combinations that have at least one
     * non-base breakpoint style but no corresponding base (media === "") style.
     *
     * Each entry includes the full list of breakpoints that DO have a value set,
     * so the caller can see what exists and suggest a base value.
     *
     * @param nodeId   - Limit to a single element; omit for all nodes.
     * @param filter.property - Only return entries matching this property.
     * @param filter.pseudo   - Only return entries matching this pseudo.
     * @param filter.media    - Only return entries that have a style at this media.
     */
    public getMissingBaseStyles(
        nodeId?: string,
        filter?: { property?: string; pseudo?: string; media?: string },
    ): Map<string, Array<{
        property: string;
        pseudo: string;
        theme: string;
        breakpoints: Array<{ media: string; value: string }>;
    }>> {
        type MissingEntry = { property: string; pseudo: string; theme: string; breakpoints: Array<{ media: string; value: string }> };
        const result = new Map<string, MissingEntry[]>();

        const nodeIds = nodeId ? [nodeId] : Array.from(this.builderStyles.keys());

        for (const id of nodeIds) {
            const styles = this.builderStyles.get(id);
            if (!styles || styles.length === 0) continue;

            const missing: MissingEntry[] = [];
            const seen = new Set<string>();

            for (const style of styles) {
                if (style.media === "" || style.value == null) continue;
                if (filter?.property !== undefined && style.property !== filter.property) continue;
                if (filter?.pseudo !== undefined && style.pseudo !== filter.pseudo) continue;

                const key = `${style.property}|${style.pseudo}|${style.theme}`;
                if (seen.has(key)) continue;
                seen.add(key);

                const hasBase = styles.some(
                    s => s.property === style.property &&
                         s.pseudo === style.pseudo &&
                         s.theme === style.theme &&
                         s.media === "" &&
                         s.value != null,
                );
                if (hasBase) continue;

                const breakpoints = styles
                    .filter(s =>
                        s.property === style.property &&
                        s.pseudo === style.pseudo &&
                        s.theme === style.theme &&
                        s.media !== "" &&
                        s.value != null,
                    )
                    .map(s => ({ media: s.media, value: s.value as string }));

                if (filter?.media !== undefined && !breakpoints.some(b => b.media === filter.media)) continue;

                missing.push({ property: style.property, pseudo: style.pseudo, theme: style.theme, breakpoints });
            }

            if (missing.length > 0) {
                result.set(id, missing);
            }
        }

        return result;
    }

    // ─── Fonts ────────────────────────────────────────────────────────────────

    public addFont(font: TreeFont): void {
        if(!font.id?.trim() || !font.name?.trim()){
            return;
        }
        this.fonts.set(font.id, font);
        this.emit({ type: "fontAdded", nodeId: "root", fontId: font.id, fontName: font.name });
    }

    public removeFont(fontId: string): void {
        const font = this.fonts.get(fontId);
        if (!font) {
            return;
        }
        this.fonts.delete(fontId);
        this.emit({ type: "fontRemoved", nodeId: "root", fontId, fontName: font.name });
    }

    // ─── Unified patch dispatch ───────────────────────────────────────────────
    //
    // All mutations from DomSession pass through here, both optimistic local
    // patches (seq: 0) and server-broadcast patches (seq > 0).

    public applyPatch(patch: TreePatch): DomChangeEvent | null {
        // Seq continuity check applies only to server-stamped patches (seq > 0).
        if (patch.seq > 0) {
            if (patch.seq > this.seq + 1) {
                this.emit({
                    type: "catchUpNeeded",
                    nodeId: patch.nodeId,
                    currentSeq: this.seq,
                    receivedSeq: patch.seq,
                });
                return null;
            }
        }

        let result: DomChangeEvent | null = null;

        switch (patch.type) {
            case "add": {
                if (!patch.parentId) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                // Remote patches arrive with node: null and affectedNodes populated.
                const rootNode = patch.node ?? this.rebuildFromFlat(patch.nodeId, patch.affectedNodes);
                if (!rootNode) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                if (patch.nodeId !== rootNode.id) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                if (patch.deleteFirst && this.nodes.has(patch.nodeId)) {
                    this.removeNode(patch.nodeId);
                }
                restoreNodeMaps(rootNode);
                result = this.addNode(patch.parentId, rootNode, patch.siblingBeforeId, patch.siblingAfterId);
                break;
            }

            case "remove": {
                result = this.removeNode(patch.nodeId);
                break;
            }

            case "move": {
                if (!patch.parentId) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.moveNode(patch.nodeId, patch.parentId, patch.siblingBeforeId, patch.siblingAfterId);
                break;
            }

            case "updateAttribute": {
                if (!patch.propertyName || patch.versionedValue === undefined) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.updateAttribute(
                    patch.nodeId,
                    patch.propertyName,
                    patch.versionedValue as VersionedValue<string | null>,
                );
                break;
            }

            case "updateClass": {
                if (!patch.propertyName || patch.versionedValue === undefined) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.updateClass(
                    patch.nodeId,
                    patch.propertyName,
                    patch.versionedValue as VersionedValue<boolean>,
                );
                break;
            }

            case "updateText": {
                if (patch.versionedValue === undefined) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.updateText(patch.nodeId, patch.versionedValue as VersionedValue<string | null>);
                break;
            }

            case "updateTagName": {
                if (patch.versionedValue === undefined) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.updateTagName(patch.nodeId, patch.versionedValue as VersionedValue<string>);
                break;
            }

            case "updateComment": {
                if (patch.versionedValue === undefined) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.updateComment(patch.nodeId, patch.versionedValue as VersionedValue<string | null>);
                break;
            }

            case "setBuilderStyle": {
                if (
                    !patch.propertyName ||
                    patch.pseudo === undefined ||
                    patch.media === undefined ||
                    patch.theme === undefined ||
                    patch.versionedValue === undefined
                ) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                const style: NodeStyle = {
                    nodeId: patch.nodeId,
                    property: patch.propertyName,
                    value: patch.versionedValue.value as string | undefined,
                    pseudo: patch.pseudo,
                    media: patch.media,
                    theme: patch.theme,
                    version: patch.versionedValue.version,
                    seq: patch.versionedValue.seq,
                    updatedAt: Date.now(),
                };
                result = this.setBuilderStyle(style);
                break;
            }

            case "removeBuilderStyle": {
                if (
                    !patch.propertyName ||
                    patch.pseudo === undefined ||
                    patch.media === undefined ||
                    patch.theme === undefined ||
                    patch.versionedValue === undefined
                ) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.removeBuilderStyle(
                    patch.nodeId,
                    patch.propertyName,
                    patch.pseudo,
                    patch.media,
                    patch.theme,
                    patch.versionedValue.version,
                );
                break;
            }

            case "setCustomStyles": {
                if (!patch.propertyName || patch.versionedValue === undefined) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                result = this.updateCustomStyle(
                    patch.nodeId,
                    patch.propertyName,
                    patch.versionedValue as VersionedValue<string>,
                );
                break;
            }

            case "addFont": {
                if (!patch.fontId || !patch.fontName) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                this.addFont({ id: patch.fontId, name: patch.fontName, contentUrl: "" });
                result = { type: "fontAdded", nodeId: "root", fontId: patch.fontId, fontName: patch.fontName };
                break;
            }

            case "removeFont": {
                if (!patch.fontId) {
                    this.emitValidationFailed(patch.nodeId, "InvalidPatch");
                    return null;
                }
                this.removeFont(patch.fontId);
                result = { type: "fontRemoved", nodeId: "root", fontId: patch.fontId };
                break;
            }

            default: {
                // Unknown type — fall through so seq still advances and doesn't stall.
                break;
            }
        }

        // Advance the seq cursor for every server-stamped patch that arrives in order,
        // regardless of whether the apply succeeded — otherwise a failing patch stalls
        // the seq and every subsequent remote patch triggers an infinite catchUpNeeded loop.
        if (patch.seq > 0 && patch.seq === this.seq + 1) {
            this.seq = patch.seq;
        }

        return result;
    }

    // ─── Snapshot loading ─────────────────────────────────────────────────────

    /**
     * Replaces the entire tree with the contents of a server snapshot.
     * Restores Maps after JSON round-trip and sorts all children by position.
     */
    public loadFromSnapshot(snapshot: TreeSnapshot): void {
        restoreNodeMaps(snapshot.root);

        this.nodes.clear();
        const register = (node: TreeNode) => {
            this.nodes.set(node.id, node);
            for (const child of node.children ?? []) {
                register(child);
            }
        };
        register(snapshot.root);

        this.root = snapshot.root;
        this.seq = snapshot.seq;

        for (const node of this.nodes.values()) {
            if (node.children.length > 1) {
                node.children.sort((a, b) => {
                    if (a.position < b.position) return -1;
                    if (a.position > b.position) return 1;
                    return 0;
                });
            }
        }

        this.builderStyles.clear();
        for (const nodeId of Object.keys(snapshot.builderStyles)) {
            this.builderStyles.set(nodeId, snapshot.builderStyles[nodeId]);
        }

        this.fonts.clear();
        for (const fontId of Object.keys(snapshot.fonts)) {
            this.fonts.set(fontId, snapshot.fonts[fontId]);
        }
    }

    // ─── Lookup ───────────────────────────────────────────────────────────────

    public findNodeById(id: string): TreeNode | undefined {
        return this.nodes.get(id);
    }

    public getNodeCount(): number {
        return this.nodes.size;
    }

    // ─── Private helpers ──────────────────────────────────────────────────────

    private rebuildFromFlat(rootId: string, nodes?: TreeNode[]): TreeNode | null {
        if (!nodes?.length) return null;
        const byId = new Map<string, TreeNode>();
        for (const n of nodes) {
            byId.set(n.id, { ...n, children: [] });
        }
        for (const n of byId.values()) {
            if (n.id === rootId) continue;
            const parent = byId.get(n.parentId);
            if (parent) parent.children.push(n);
        }
        for (const n of byId.values()) {
            if (n.children.length > 1) {
                n.children.sort((a, b) => a.position < b.position ? -1 : a.position > b.position ? 1 : 0);
            }
        }
        return byId.get(rootId) ?? null;
    }

    private resolveInsertIndex(
        parent: TreeNode,
        siblingBeforeId: string | undefined,
        siblingAfterId: string | undefined,
    ): number {
        if (siblingBeforeId?.trim()) {
            const beforeIndex = parent.children.findIndex(child => child.id === siblingBeforeId);
            if (beforeIndex !== -1) {
                return beforeIndex + 1;
            }
        }
        if (siblingAfterId?.trim()) {
            const afterIndex = parent.children.findIndex(child => child.id === siblingAfterId);
            if (afterIndex !== -1) {
                return afterIndex;
            }
        }
        return parent.children.length;
    }

    private registerSubtree(node: TreeNode): void {
        this.nodes.set(node.id, node);
        for (const child of node.children) {
            this.registerSubtree(child);
        }
    }

    private unregisterSubtree(node: TreeNode): void {
        for (const child of node.children) {
            this.unregisterSubtree(child);
        }
        node.parentId = "";
        this.nodes.delete(node.id);
        this.builderStyles.delete(node.id);
    }

    private findSubtreeCollision(node: TreeNode): string | null {
        if (this.nodes.has(node.id)) {
            return node.id;
        }
        for (const child of node.children) {
            const hit = this.findSubtreeCollision(child);
            if (hit !== null) {
                return hit;
            }
        }
        return null;
    }
}

// ─── Cascade resolution ────────────────────────────────────────────────────────

/**
 * Builds the ordered chain of `media` keys to check for a requested
 * breakpoint, from most specific to least - the same order IframeBridge's
 * compiled CSS cascade resolves in:
 *   - Breakpoints narrower than the base device cascade DOWN toward it
 *     (bp itself, then progressively wider below-base breakpoints, then base).
 *   - Breakpoints wider than the base device cascade UP toward it
 *     (bp itself, then progressively narrower above-base breakpoints, then base).
 *   - The base device itself (`media === ""`) has no chain beyond itself.
 *
 * `knownMedias` should be every distinct non-base `media` value present for
 * the node (regardless of property/pseudo/theme) — candidates without an
 * actual matching entry are simply skipped by the caller, so over-including
 * irrelevant breakpoints here is harmless as long as the real ones are in the
 * right order.
 *
 * Falls back to "bp, then base" when `baseWidth` is unknown (`<= 0`) or when
 * `media` isn't a parseable breakpoint width.
 */
export function cascadeMediaCandidates(media: string, baseWidth: number, knownMedias: Set<string>): string[] {
    if (!media) return [""];
    const bp = parseFloat(media);
    if (isNaN(bp)) return [media, ""];

    const isAbove = baseWidth > 0 && bp > baseWidth;
    const widths = Array.from(knownMedias)
        .map(m => parseFloat(m))
        .filter(w => !isNaN(w) && w > 0);

    const between = Array.from(
        new Set(
            widths.filter(w =>
                isAbove ? w > baseWidth && w < bp : baseWidth > 0 ? w < baseWidth && w > bp : w > bp,
            ),
        ),
    );
    // Order from closest-to-bp toward the base: descending for the above-base
    // group (walking down toward base), ascending for the below-base group
    // (walking up toward base).
    between.sort((a, b) => (isAbove ? b - a : a - b));

    return [String(bp), ...between.map(String), ""];
}
